import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  effect,
  inject,
  Input,
  OnInit,
  signal,
  TemplateRef,
} from '@angular/core';
import { ActionsBarOptions, TreeListAction } from '@app/orders/models/dx-tree-list.model';
import { TreeListRowData } from '@app/orders/models/orders.model';
import { Constants } from '@core/app-constants';
import { BtnGroupItem } from '@shared/components/btn-group/btn-group.component';
import { OrdersStatusService } from '@app/orders/services/orders-status.service';
import { buildTreeListActionOverflowItems } from '../tree-list-action-mapper';

@Component({
  selector: 'app-tree-list-action-bar',
  templateUrl: './tree-list-action-bar.component.html',
  styleUrls: ['./tree-list-action-bar.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeListActionBarComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly statusService = inject(OrdersStatusService);
  private readonly printableRefreshEffect = effect(() => {
    this.statusService.productPrintableVersion();
    this.evaluateActions();
  });

  @ContentChild('selectionSummary') selectionSummaryTemp: TemplateRef<any>;
  @Input() set selection(value: TreeListRowData[]) {
    this._selection = value || [];
    this.evaluateActions();
  }
  @Input() set actions(value: TreeListAction[]) {
    this._allActions = value;
    this.evaluateActions();
  }
  @Input() set fixedActions(value: TreeListAction[]) {
    this._allfixedActions = value;
    this.evaluateActions();
  }
  @Input() set actionsBarOptions(value: ActionsBarOptions) {
    this._actionsBarOptions = value;
    this.evaluateActions();
  }
  _actionsBarOptions: ActionsBarOptions = {
    width: '',
    label: 'item(s)',
    slice: Constants.SliceActionsNumber,
  };
  public _actions: TreeListAction[];
  public _allActions: TreeListAction[];
  public _allfixedActions: TreeListAction[];
  public _fixedActions: TreeListAction[];
  public _selection: TreeListRowData[];
  public _buttonGroupActions: BtnGroupItem[];
  public isCheckboxSelected = true;
  public isShowAnimation = signal(false);

  ngOnInit(): void {
    setTimeout(() => {
      this.isShowAnimation.set(true);
      this.changeDetector.detectChanges();
    }, 100);
  }

  private evaluateActions() {
    this._actions = (this._allActions || []).filter(
      (action) => !!action?.allItemsCommand && !this.isActionHidden(action),
    );

    this._fixedActions = (this._allfixedActions || []).filter(
      (action) => !!action?.allItemsCommand && !this.isActionHidden(action),
    );

    if (this._actions?.length > this._actionsBarOptions?.slice) {
      this._buttonGroupActions = this._actions
        .slice(this._actionsBarOptions.slice)
        .flatMap((action) => this.mapOverflowItems(action));
    } else {
      this._buttonGroupActions = [];
    }
  }

  private mapOverflowItems(action: TreeListAction): BtnGroupItem[] {
    const label = action.selectionLabel?.(this._selection) ?? action.label ?? '';
    if (!action.allItemsCommand) return [];

    return buildTreeListActionOverflowItems({
      action,
      context: this._selection,
      label,
      onAction: () => action.allItemsCommand?.(this._selection),
    });
  }

  private isActionHidden(action: TreeListAction) {
    return (
      (typeof action?.allItemsHidden === 'function' && action.allItemsHidden(this._selection)) ||
      (!action?.allItemsIgnoreHidden &&
        typeof action?.hidden === 'function' &&
        (this._selection || []).some((data: any) => action.hidden(data))) ||
      (action?.allItemsIgnoreHidden &&
        this._selection?.length === 1 &&
        typeof action?.hidden === 'function' &&
        action.hidden(this._selection[0]))
    );
  }
}
